import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import FullButton from './FullButton';
import { useSidePanel } from '../../contexts/SidePanelContext';
import { DebugCaptureResult, DebugRequestPanel } from './DebugRequestPanel';

interface ErrorDetail {
  target: string;
  message: string;
}

interface PublishErrorProps {
  error: {
    message: string;
    details?: ErrorDetail[];
  };
  onEditListing: () => void;
  showEditButton?: boolean;
  debugInfo?: DebugCaptureResult;
}

const PublishError: React.FC<PublishErrorProps> = ({
  error,
  onEditListing,
  showEditButton = true,
  debugInfo,
}) => {
  const [debugExpanded, setDebugExpanded] = useState(false);
  const { setPanelWidth } = useSidePanel();

  const handleDebugToggle = () => {
    const next = !debugExpanded;
    setDebugExpanded(next);
    setPanelWidth(next ? 1000 : 375);
  };

  return (
    <Box sx={{ mx: 1.5 }}>
      <Alert severity="error" sx={{ mb: 2, mt: 3 }}>
        <Typography>{error.message}</Typography>
      </Alert>

      {error?.details && Array.isArray(error.details) && (
        <Box sx={{ mt: 2, mb: 2 }}>
          {error.details.map((detail: ErrorDetail, index: number) => (
            <Box
              key={index}
              sx={{
                mb: 1.5,
                pl: 2,
                borderLeft: '4px solid',
                borderLeftColor: 'error.light',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  mb: 0.5,
                }}
              >
                {detail.target}
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.600' }}>
                {detail.message}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Debug Information Accordion */}
      {debugInfo && (
        <Accordion
          expanded={debugExpanded}
          onChange={handleDebugToggle}
          sx={{
            mt: 2,
            mb: showEditButton ? 2 : 0,
            '&:before': { display: 'none' },
            boxShadow: 1,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: 'grey.100',
              '&:hover': { backgroundColor: 'grey.200' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BugReportIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                Debug Information (
                {debugInfo.main.length + debugInfo.other.length} requests)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <DebugRequestPanel debugInfo={debugInfo} title="Publish Debug" />
          </AccordionDetails>
        </Accordion>
      )}

      {showEditButton && (
        <Box sx={{ mb: debugInfo ? 2 : 10 }}>
          <FullButton text={'Edit Listing'} onClick={onEditListing} />
        </Box>
      )}
    </Box>
  );
};

export default PublishError;
